// Pagina autenticazione a due fattori TOTP
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import { useAuth } from '../../hooks/useAuth'
import { useAuthStore } from '../../stores/auth.store'

const totpSchema = z.object({
  code: z.string().length(6, 'Il codice deve essere di 6 cifre').regex(/^\d+$/, 'Solo cifre'),
})

type TotpFormData = z.infer<typeof totpSchema>

export function TwoFactorPage() {
  const navigate = useNavigate()
  const { login, isLoginLoading, loginError } = useAuth()
  const pendingEmail = useAuthStore((s) => s.pendingEmail)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TotpFormData>({
    resolver: zodResolver(totpSchema),
  })

  // Se non c'è email pendente, torna al login
  if (!pendingEmail) {
    navigate('/login')
    return null
  }

  const onSubmit = (data: TotpFormData) => {
    login({ email: pendingEmail, password: '', totpCode: data.code })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--fc-background)] p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--fc-primary)]">
            <Shield className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--fc-foreground)]">Verifica identità</h1>
          <p className="mt-1 text-sm text-[var(--fc-foreground-muted)]">
            Inserisci il codice dalla tua app di autenticazione
          </p>
        </div>

        <div className="rounded-xl border border-[var(--fc-border)] bg-[var(--fc-surface)] p-6 shadow-[var(--fc-shadow-md)]">
          {loginError && (
            <Alert variant="error" className="mb-4">
              Codice non valido. Riprova.
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <Input
              label="Codice TOTP"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              autoComplete="one-time-code"
              className="text-center text-xl tracking-widest font-mono"
              error={errors.code?.message}
              required
              {...register('code')}
            />

            <Button type="submit" className="w-full" isLoading={isLoginLoading}>
              Verifica
            </Button>
          </form>

          <button
            onClick={() => navigate('/login')}
            className="mt-4 w-full text-center text-sm text-[var(--fc-primary)] hover:underline"
          >
            Torna al login
          </button>
        </div>
      </div>
    </div>
  )
}
